/**
 * EngagementCard Component
 * Feature 014: Service Provider Engagements
 *
 * Card component displaying consultation request/engagement details
 * Used in both Pending and Accepted engagements sections
 */

import { useState } from 'react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Textarea } from '@/shared/components/ui/textarea';
import {
  MessageCircle,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  Save,
  Phone,
  Mail,
} from 'lucide-react';
import type { ConsultationRequest } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { EngagementDescription } from './EngagementDescription';

type EngagementCardProps = {
  engagement: ConsultationRequest;
  onAccept?: (id: string) => void;
  onDecline?: (id: string) => void;
  onUpdateNotes?: (id: string, notes: string) => void;
  onViewConversation?: (careSeekerUserId: string) => void;
  isAccepting?: boolean;
  isDeclining?: boolean;
  isUpdatingNotes?: boolean;
};

export function EngagementCard({
  engagement,
  onAccept,
  onDecline,
  onUpdateNotes,
  onViewConversation,
  isAccepting = false,
  isDeclining = false,
  isUpdatingNotes = false,
}: EngagementCardProps) {
  const [notes, setNotes] = useState(engagement.providerNotes || '');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const isPending = engagement.status === 'PENDING';
  const isAccepted = engagement.status === 'ACCEPTED';
  const isConsultationRequest = engagement.leadSource === 'CONSULTATION_REQUEST';
  const isCalendlyBooking = engagement.leadSource === 'CALENDLY_BOOKING';
  const isIntakeFormOpen = engagement.leadSource === 'INTAKE_FORM_OPEN';
  const isCallRequest = engagement.leadSource === 'CALL_REQUEST';

  const timeAgo = formatDistanceToNow(new Date(engagement.createdAt), {
    addSuffix: true,
  });

  const handleSaveNotes = () => {
    if (onUpdateNotes && notes !== engagement.providerNotes) {
      onUpdateNotes(engagement.id, notes);
    }
    setIsEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setNotes(engagement.providerNotes || '');
    setIsEditingNotes(false);
  };

  return (
    <Card className="p-6">
      <div className="flex gap-4">
        {/* Care Seeker Photo */}
        <div className="flex-shrink-0">
          {engagement.careSeeker?.profilePhoto ? (
            <img
              src={engagement.careSeeker.profilePhoto}
              alt={`${engagement.careSeeker.firstName} ${engagement.careSeeker.lastName}`}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-[#026857] flex items-center justify-center">
              <span className="text-white text-xl font-semibold">
                {engagement.careSeeker?.firstName?.charAt(0) || '?'}
              </span>
            </div>
          )}
        </div>

        {/* Engagement Details */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {engagement.careSeeker?.firstName} {engagement.careSeeker?.lastName}
              </h3>
              <p className="text-sm text-gray-500">{timeAgo}</p>
            </div>
            <div className="flex flex-col gap-2 items-end">
              {isConsultationRequest && (
                <Badge className="bg-[#378F94] hover:bg-[#378F94]">
                  <FileText className="w-3 h-3 mr-1" />
                  Consultation Requested
                </Badge>
              )}
              {isCalendlyBooking && (
                <Badge className="bg-[#4F46E5] text-white hover:bg-[#4F46E5]">
                  <Calendar className="w-3 h-3 mr-1" />
                  Calendly Booking
                </Badge>
              )}
              {isIntakeFormOpen && (
                <Badge className="bg-[#026857] text-white hover:bg-[#026857]">
                  <FileText className="w-3 h-3 mr-1" />
                  Intake Form
                </Badge>
              )}
              {isCallRequest && (
                <Badge className="bg-[#E2A807] text-white hover:bg-[#E2A807]">
                  <Phone className="w-3 h-3 mr-1" />
                  Call Requested
                </Badge>
              )}
              {isAccepted && engagement.isExistingClient && (
                <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Existing Client
                </Badge>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <EngagementDescription
              description={engagement.description}
              isCalendlyBooking={isCalendlyBooking}
              isIntakeFormOpen={isIntakeFormOpen}
            />
          </div>

          {/* Availability (only for consultation requests) */}
          {isConsultationRequest && engagement.availabilityTimes.length > 0 && (
            <div className="mb-4 space-y-2">
              {/* Times */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400" />
                <div className="flex flex-wrap gap-2">
                  {engagement.availabilityTimes.map((time) => (
                    <Badge key={time} variant="outline" className="text-xs">
                      {time.charAt(0).toUpperCase() + time.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Days */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div className="flex flex-wrap gap-2">
                  {engagement.availabilityDays.map((day) => (
                    <Badge key={day} variant="outline" className="text-xs">
                      {day.charAt(0).toUpperCase() + day.slice(1).substring(0, 3)}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Feature 102: callback details for call requests */}
          {isCallRequest && (
            <div className="mb-4 space-y-1">
              {engagement.contactPhone && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${engagement.contactPhone}`} className="hover:underline">
                    {engagement.contactPhone}
                  </a>
                </div>
              )}
              {engagement.contactEmail && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${engagement.contactEmail}`} className="hover:underline">
                    {engagement.contactEmail}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Message Engagement Indicator */}
          {!isConsultationRequest && !isIntakeFormOpen && !isCallRequest && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 italic">
                Started a conversation - view in Messages
              </p>
            </div>
          )}

          {/* Provider Notes (Accepted engagements only) */}
          {isAccepted && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Private Notes (only you can see this)
                </label>
                {!isEditingNotes && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingNotes(true)}
                  >
                    Edit
                  </Button>
                )}
              </div>
              {isEditingNotes ? (
                <div className="space-y-2">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this consultation request..."
                    rows={3}
                    className="resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveNotes}
                      disabled={isUpdatingNotes}
                      className="bg-[#026857] hover:bg-[#025247]"
                    >
                      <Save className="w-4 h-4 mr-1" />
                      {isUpdatingNotes ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelNotes}
                      disabled={isUpdatingNotes}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                  {notes || 'No notes yet'}
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 flex-wrap">
            {isPending && !isIntakeFormOpen && (
              <>
                <Button
                  size="sm"
                  onClick={() => onAccept?.(engagement.id)}
                  disabled={isAccepting || isDeclining}
                  className="bg-[#026857] hover:bg-[#025247]"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  {isAccepting ? 'Accepting...' : 'Accept'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDecline?.(engagement.id)}
                  disabled={isAccepting || isDeclining}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  {isDeclining ? 'Declining...' : 'Decline'}
                </Button>
              </>
            )}

            {/* View Conversation button — hidden for intake-form opens and
                call requests (neither creates a message thread; the SP calls
                the number on the card instead). */}
            {onViewConversation && engagement.careSeeker && !isIntakeFormOpen && !isCallRequest && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onViewConversation(engagement.careSeeker!.id)}
              >
                <MessageCircle className="w-4 h-4 mr-1" />
                View Conversation
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
